import { useEffect, useRef } from "react";

/**
 * Silent background polling for dashboards.
 *
 * - Always invokes the LATEST `onTick` via a ref, so the callback reads fresh
 *   component state (page, page-size, filters) without re-creating the interval
 *   on every render.
 * - Skips a tick while the browser tab is hidden (`document.hidden`).
 * - `enabled` lets callers pause polling (e.g. while a dialog is open).
 *
 * The tick is expected to re-dispatch the view's existing fetches with a
 * `silent` flag so no global loader/spinner is shown.
 */
export const useAutoRefresh = (
  onTick: () => void,
  {
    intervalMs = 30000,
    enabled = true,
  }: { intervalMs?: number; enabled?: boolean } = {}
) => {
  const saved = useRef(onTick);

  useEffect(() => {
    saved.current = onTick;
  });

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      if (!document.hidden) saved.current();
    }, intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, enabled]);
};
